import React, { useContext, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowPathIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { Button } from "../../../components/forms/Buttons/Button";
import CustomDialog from "../../../components/modals/CustomDialog/CustomDialog";
import { getStatuses } from "../../../services/questionService";
import { changeQuestionStatus } from "../../../services/reviewService";
import { getApiErrorCode, getCodedErrorMessage, listedItems } from "../../../utils/apiError";
import { UserContext } from "../../../utils/contexts/UserContext";
import { isAdminUser } from "../../../utils/folders";
import { cardClass } from "../../Exams/TestBuilder/ui";
import { BookletLinks, bookletLockMessage, linksBooklets, lockingBooklets, questionBooklets } from "./bookletLock";
import CommentField from "./CommentField";
import { COMMENT_LIMIT, cleanComment, reviewSteps, statusLabel, transitionKey } from "./reviewUtils";
import { stepLook } from "./stepLooks";

// A step to choose is a button in the colour of where it leads, with its icon; the chosen one filled. The radio
// itself is hidden; its label is the target, with the radio's focus ring. 44px on phones.
const choiceClass = (code, chosen) => {
  const look = stepLook(code);
  return `inline-flex min-h-[2.75rem] cursor-pointer items-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm ring-1 ring-inset transition duration-200 active:scale-[0.98] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-60 md:min-h-[2.5rem] ${
    chosen ? look.on : `${look.idle} ${look.hover}`
  }`;
};

// Why a comment is needed, by the step that needs it.
const COMMENT_HINTS = {
  APPROVED: "question_detail.review.approve.hint",
  CHANGES_REQUESTED: "question_detail.comment.required_hint",
  DECLINED: "question_detail.review.decline_hint",
};
// An approval asks first what was checked (each ticked) and why the question is approved (the comment, required
// here: the server takes an approval without one).
const APPROVE_CHECKS = ["text", "answer", "topic", "level"];

// Returning an approved question that tests of an "approved only" exam hold: it would stay in them unapproved.
// A BTS subject variant the user does not compose comes unnamed and is named in the user's language.
function InTestsDialog({ prompt, sending, onCancel, onConfirm }) {
  const { t } = useTranslation();
  return (
    <CustomDialog
      isOpen={prompt.open}
      closeModal={() => !sending && onCancel()}
      headerText={t("question_detail.review.in_tests.title")}
      maxWidth="max-w-lg"
    >
      <p className="mt-2 text-sm text-slate-600">{t("question_detail.review.in_tests.text")}</p>
      <ul className="mt-3 max-h-60 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-3 text-sm text-slate-800 ring-1 ring-inset ring-slate-200">
        {listedItems(prompt.tests, t).map((test) => (
          <li key={test.key} className="flex gap-2">
            <span className="text-slate-400" aria-hidden="true">
              •
            </span>
            <span className="min-w-0 break-words">{test.label}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm font-medium text-slate-900">{t("question_detail.review.in_tests.question")}</p>
      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          color="secondary-outline"
          text={t("buttons.cancel")}
          onClick={onCancel}
          disabled={sending}
          className="max-md:h-11"
        />
        <Button
          type="button"
          color="primary"
          text={t("question_detail.review.in_tests.confirm")}
          onClick={onConfirm}
          loading={sending}
          className="max-md:h-11"
        />
      </div>
    </CustomDialog>
  );
}

/**
 * The reviewer's decision: one choice per step of the detail's actions.transitions (an old backend: the old four),
 * labelled by where it leads; once one is chosen, a comment (required for actions.comment_required, and for an
 * approval, which also asks to tick what was checked) and the confirmation, which names it. `onChanged` reads the question and its history again after a change, `onReload`
 * when the page is out of date. A refusal for an active BTS booklet (activated while the page was open) has the page
 * read the question at once: the lock notice comes up front, the steps go, and the refusal stays here with the focus.
 */
export default function DecisionPanel({ question, folderRole = null, onChanged, onReload }) {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const commentRef = useRef(null);
  const errorRef = useRef(null);
  const choiceName = useId();
  // {code: status uuid}: update_status takes the status by its uuid.
  const [statusUuids, setStatusUuids] = useState(null);
  const [selected, setSelected] = useState("");
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [inTests, setInTests] = useState({ open: false, tests: [] });
  // The approval's checks ticked.
  const [checks, setChecks] = useState(() => new Set());

  const current = question.status?.code;
  const { transitions, commentRequired: commentRequiredFor } = reviewSteps(question);
  const transitionsKey = transitions.join();

  useEffect(() => {
    const controller = new AbortController();
    getStatuses({ params: { page: 1, page_size: 100 }, signal: controller.signal })
      .then((data) => setStatusUuids(Object.fromEntries((data?.results || data || []).map((s) => [s.code, s.uuid]))))
      .catch(() => {
        if (!controller.signal.aborted) setStatusUuids({});
      });
    return () => controller.abort();
  }, []);

  // A reload may take the chosen step away (someone else changed the status).
  useEffect(() => {
    if (selected && !transitionsKey.split(",").includes(selected)) setSelected("");
  }, [transitionsKey, selected]);

  // A refusal whose buttons go away with the reload (the booklet lock) takes the focus they had.
  useEffect(() => {
    if (error?.focus) errorRef.current?.focus();
  }, [error]);

  // Nobody decides on their own question in a subject (admins do): its moderator archives or restores it only.
  const own =
    !!question.folder && !!user?.uuid && question.author?.uuid === user.uuid && folderRole !== "admin" && !isAdminUser(user);
  const approving = selected === "APPROVED";
  const commentRequired = commentRequiredFor.includes(selected) || approving;
  const commentMissing = commentRequired && !cleanComment(comment);
  const checksMissing = approving && APPROVE_CHECKS.some((key) => !checks.has(key));
  const stepLabel = (code) => t(`question_detail.transitions.${transitionKey(code, current)}`);

  const choose = (code) => {
    setSelected(code);
    setChecks(new Set());
    setError(null);
    setCommentError("");
  };
  const toggleCheck = (key) =>
    setChecks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const submit = async (confirm = false) => {
    if (!selected || sending || checksMissing) return;
    if (commentMissing) {
      commentRef.current?.focus();
      return;
    }
    const label = statusLabel(t, { code: selected });
    const failed = t("question_detail.toast.status_change_failed", { status: label });
    const status = statusUuids?.[selected];
    setError(null);
    setCommentError("");
    if (!status) {
      setError({ message: failed });
      return;
    }
    setSending(true);
    try {
      await changeQuestionStatus(question.uuid, { status, comment: cleanComment(comment), confirm });
      toast.success(t("question_detail.toast.status_changed", { status: label }));
      setInTests((prompt) => ({ ...prompt, open: false }));
      setSelected("");
      setComment("");
      onChanged();
    } catch (err) {
      const code = getApiErrorCode(err);
      const data = err?.response?.data;
      if (code === "question_in_tests" && !confirm) {
        setInTests({ open: true, tests: Array.isArray(data?.tests) ? data.tests : [] });
        return;
      }
      setInTests((prompt) => ({ ...prompt, open: false }));
      if (code === "comment_required" || Array.isArray(data?.comment)) {
        setCommentError(
          code === "comment_required" ? t("errors.comment_required") : t("question_detail.review.comment_too_long", { max: COMMENT_LIMIT })
        );
        commentRef.current?.focus();
        return;
      }
      if (code === "question_in_booklet") {
        // An active BTS booklet prints the question: said with the way out (an admin gets the booklets' links,
        // which name them), and the page reads the question again, so that nothing offers a change that can only
        // fail (its steps, Edit, the delete) and the lock notice comes up front.
        const booklets = lockingBooklets(err);
        const linked = linksBooklets(user, booklets);
        setError({
          message: bookletLockMessage(
            t,
            linked ? "question_detail.booklet_lock.change_linked" : "question_detail.booklet_lock.change",
            booklets,
            linked,
            { counted: true }
          ),
          booklets: linked ? booklets : [],
          focus: true,
        });
        onReload();
        return;
      }
      // The status is not what the page shows any more: offer to read it again.
      setError({
        message: getCodedErrorMessage(t, err, failed),
        reload: code === "transition_not_allowed" || code === "draft_status",
      });
    } finally {
      setSending(false);
    }
  };

  const reload = () => {
    setError(null);
    setSelected("");
    onReload();
  };

  // A question an active BTS booklet prints has no decision to offer, as on its reading page: the lock notice up
  // front says why (e.g. after its delete was refused). A refusal of a decision for it stays, with the focus it took.
  if (questionBooklets(question).length && !error) return null;

  return (
    <section aria-labelledby="decision-title" className={cardClass}>
      <div className="p-5 sm:p-6">
        <h3 id="decision-title" className="text-lg font-semibold tracking-tight text-slate-900">
          {t("question_detail.decision.title")}
        </h3>

        {own && (
          <p className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900 ring-1 ring-inset ring-blue-600/10">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
            {t("question_detail.review.own_question_note")}
          </p>
        )}

        {transitions.length === 0 ? (
          // A refusal below says why already (the booklet lock).
          !own &&
          !error && (
            <p className="mt-2 text-sm text-slate-500">
              {current === "DRAFT" ? t("errors.draft_status") : t("question_detail.review.no_transitions")}
            </p>
          )
        ) : (
          <>
            {/* Radios: Tab reaches the group, arrows move between the steps. They wrap like a toolbar (Kazakh labels
                are long), phones included. */}
            <fieldset disabled={sending} className="mt-2">
              <legend className="text-sm text-slate-500">{t("question_detail.decision.select")}</legend>
              <div className="mt-3 flex flex-wrap gap-2">
                {transitions.map((code) => {
                  const { Icon } = stepLook(code);
                  return (
                    <label key={code} className="relative">
                      <input
                        type="radio"
                        name={choiceName}
                        value={code}
                        checked={selected === code}
                        onChange={() => choose(code)}
                        className="peer sr-only"
                      />
                      <span className={choiceClass(code, selected === code)}>
                        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {stepLabel(code)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* The comment and the confirmation come with a chosen step; the button names it. */}
            {selected && (
              <div className="mt-5 space-y-4">
                {approving && (
                  <div
                    role="group"
                    aria-labelledby="approve-checks-title"
                    className="rounded-lg bg-emerald-50/70 px-3 py-2.5 ring-1 ring-inset ring-emerald-600/15"
                  >
                    <p id="approve-checks-title" className="text-sm font-medium text-slate-900">
                      {t("question_detail.review.approve.checks_title")}
                    </p>
                    <ul className="mt-1.5 space-y-0.5">
                      {APPROVE_CHECKS.map((key) => (
                        <li key={key}>
                          <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-3 rounded-md px-1 hover:bg-emerald-100/50 max-sm:min-h-11">
                            <input
                              type="checkbox"
                              checked={checks.has(key)}
                              onChange={() => toggleCheck(key)}
                              disabled={sending}
                              className="h-4 w-4 shrink-0 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600"
                            />
                            <span className="text-sm text-slate-800">{t(`question_detail.review.approve.check_${key}`)}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <CommentField
                  ref={commentRef}
                  id="decisionComment"
                  label={
                    approving
                      ? t("question_detail.review.approve.reason_label")
                      : commentRequired
                        ? t("question_detail.review.comment_required_label")
                        : t("question_detail.comment.label")
                  }
                  placeholder={approving ? t("question_detail.review.approve.reason_placeholder") : t("question_detail.comment.decision_placeholder")}
                  value={comment}
                  onChange={(value) => {
                    setComment(value);
                    setCommentError("");
                  }}
                  required={commentRequired}
                  hint={COMMENT_HINTS[selected] ? t(COMMENT_HINTS[selected]) : ""}
                  error={commentError}
                />
                <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                  {approving && (checksMissing || commentMissing) && (
                    <p className="text-xs text-slate-500 max-sm:w-full">{t("question_detail.review.approve.still_needed")}</p>
                  )}
                  <Button
                    text={t(`question_detail.review.confirm.${transitionKey(selected, current)}`)}
                    color="primary"
                    loading={sending}
                    onClick={() => submit(false)}
                    disabled={commentMissing || checksMissing || !statusUuids}
                    className="max-sm:w-full max-md:h-11"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Outside the steps: a booklet lock's refusal stays once the reload has taken them away. */}
        {error && (
          <div
            ref={errorRef}
            role="alert"
            tabIndex={-1}
            className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 focus:outline-none"
          >
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              {error.message}
              {error.booklets && <BookletLinks booklets={error.booklets} />}
            </span>
            {error.reload && (
              <Button
                text={t("question_detail.review.reload")}
                Icon={ArrowPathIcon}
                color="secondary-outline"
                onClick={reload}
                className="!h-9 !px-3 max-md:!h-11"
              />
            )}
          </div>
        )}
      </div>

      <InTestsDialog
        prompt={inTests}
        sending={sending}
        onCancel={() => setInTests((prompt) => ({ ...prompt, open: false }))}
        onConfirm={() => submit(true)}
      />
    </section>
  );
}
